import { isPrivateTrackingContext } from "@/utils/trackingPrivacy";
import { captureHeroAttribution, getHeroAttribution } from "@/utils/heroAttribution";
import { captureLandingAttribution, getLandingAttribution } from "@/utils/landingAttribution";
import { hasAuthenticatedSession, isPublicAuthPath } from "@/utils/routeFlags";

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
  }
}

const GTM_ID = "GTM-TJR3SN2";

let gtmLoadPromise: Promise<void> | null = null;
let publicAuthInteractionArmed = false;
let publicAuthFallbackTimer: number | null = null;
let signupCompletionTracked = false;
const trackedPurchases = new Set<string>();

export interface ConfirmedPurchase {
  transaction_id: string;
  value: number;
  currency: string;
  checkout_type: "subscription" | "credit_pack";
  credits: number;
  items: Array<{ item_id: string; item_name: string; price: number; quantity: number }>;
}

/** Queue a receipt from the authenticated server; a page visit is never proof. */
export function trackConfirmedPurchase(receipt: ConfirmedPurchase): void {
  if (process.env.VUE_APP_TRACKING_ENABLED !== "on" || typeof window === "undefined" ||
      typeof document === "undefined" || isPrivateTrackingContext(window.location.pathname + window.location.search)) return;
  if (typeof navigator !== "undefined" && (navigator.doNotTrack === "1" ||
      (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl)) return;
  if (!receipt || !/^cs_[A-Za-z0-9_]{1,200}$/.test(receipt.transaction_id) ||
      !Number.isFinite(receipt.value) || receipt.value <= 0 ||
      !["USD", "THB"].includes(receipt.currency) ||
      !["subscription", "credit_pack"].includes(receipt.checkout_type) ||
      !Number.isSafeInteger(receipt.credits) || receipt.credits < 0 ||
      !Array.isArray(receipt.items) || receipt.items.length !== 1) return;
  const item = receipt.items[0];
  if (!item || typeof item.item_id !== "string" || !/^[A-Za-z0-9_-]{1,100}$/.test(item.item_id) ||
      typeof item.item_name !== "string" || !["MyCulture subscription", "MyCulture assessment credits"].includes(item.item_name) ||
      item.quantity !== 1 || item.price !== receipt.value) return;
  const key = `myculture:purchase:${receipt.transaction_id}`;
  if (trackedPurchases.has(key)) return;
  try { if (window.localStorage.getItem(key)) return; } catch (_) { /* In-memory fallback. */ }
  cleanupPublicAuthListeners();
  void ensureGtmLoadNow();
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "myculture_purchase_completed",
    transaction_id: receipt.transaction_id,
    value: receipt.value,
    currency: receipt.currency,
    checkout_type: receipt.checkout_type,
    credits: receipt.credits,
    items: [{ item_id: item.item_id, item_name: item.item_name, price: item.price, quantity: 1 }],
  });
  trackedPurchases.add(key);
  try { window.localStorage.setItem(key, "1"); } catch (_) { /* GA4 also receives the stable transaction ID. */ }
}



// Local dev servers must never report into the production GA property.
const LOCAL_HOST = /^(localhost|127\.0\.0\.1|\[::1\])$|\.localhost$/;

function injectGtmScript(): void {
  if (process.env.VUE_APP_TRACKING_ENABLED !== "on" || typeof window === "undefined" ||
      isPrivateTrackingContext(window.location.pathname + window.location.search)) return;
  if (LOCAL_HOST.test(window.location.hostname)) return;
  if (document.getElementById("gtm-script")) {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    "gtm.start": new Date().getTime(),
    event: "gtm.js",
  });

  const script = document.createElement("script");
  script.id = "gtm-script";
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  document.head.appendChild(script);
}

function scheduleGtmLoad(): Promise<void> {
  if (document.getElementById("gtm-script")) {
    gtmLoadPromise = gtmLoadPromise || Promise.resolve();
    return gtmLoadPromise;
  }

  if (gtmLoadPromise) {
    return gtmLoadPromise;
  }

  gtmLoadPromise = new Promise((resolve) => {
    const idleWindow = window as Window & {
      requestIdleCallback?: (
        callback: () => void,
        options?: { timeout?: number }
      ) => number;
    };
    const load = () => {
      injectGtmScript();
      resolve();
    };

    if (idleWindow.requestIdleCallback) {
      idleWindow.requestIdleCallback(() => load(), { timeout: 2500 });
      return;
    }

    window.setTimeout(load, 1200);
  });

  return gtmLoadPromise;
}

function ensureGtmLoadNow(): Promise<void> {
  if (document.getElementById("gtm-script")) {
    gtmLoadPromise = gtmLoadPromise || Promise.resolve();
    return gtmLoadPromise;
  }

  injectGtmScript();
  gtmLoadPromise = gtmLoadPromise || Promise.resolve();
  return gtmLoadPromise;
}

function cleanupPublicAuthListeners(): void {
  if (!publicAuthInteractionArmed) {
    return;
  }

  publicAuthInteractionArmed = false;
  window.removeEventListener("pointerdown", handlePublicAuthInteraction);
  window.removeEventListener("keydown", handlePublicAuthInteraction);
  window.removeEventListener("touchstart", handlePublicAuthInteraction);

  if (publicAuthFallbackTimer !== null) {
    window.clearTimeout(publicAuthFallbackTimer);
    publicAuthFallbackTimer = null;
  }
}

function handlePublicAuthInteraction(): void {
  cleanupPublicAuthListeners();
  void scheduleGtmLoad();
}

function armPublicAuthTracking(): void {
  if (publicAuthInteractionArmed || gtmLoadPromise) {
    return;
  }

  publicAuthInteractionArmed = true;
  window.addEventListener("pointerdown", handlePublicAuthInteraction, {
    once: true,
    passive: true,
  });
  window.addEventListener("keydown", handlePublicAuthInteraction, {
    once: true,
    passive: true,
  });
  window.addEventListener("touchstart", handlePublicAuthInteraction, {
    once: true,
    passive: true,
  });

  publicAuthFallbackTimer = window.setTimeout(() => {
    cleanupPublicAuthListeners();
    void scheduleGtmLoad();
  }, 10000);
}

export function scheduleThirdPartyBootstrap(path: string): void {
  if (process.env.VUE_APP_TRACKING_ENABLED !== "on" || typeof window === "undefined" ||
      isPrivateTrackingContext(window.location.pathname + window.location.search)) return;
  if (typeof window === "undefined" || typeof document === "undefined") {
    return;
  }

  if (isPrivateTrackingContext(path)) {
    cleanupPublicAuthListeners();
    return;
  }

  captureHeroAttribution();
  captureLandingAttribution();

  if (isPublicAuthPath(path)) {
    armPublicAuthTracking();
    return;
  }

  cleanupPublicAuthListeners();

  // Logged-in app: defer GTM until idle — marketing tags still fire, first paint stays fast.
  if (hasAuthenticatedSession()) {
    void scheduleGtmLoad();
    return;
  }

  void ensureGtmLoadNow();
}

/**
 * Queue the confirmed account-creation event for GTM.
 *
 * This intentionally contains no email, name, user ID, or other personal data.
 * The module-level guard prevents an accidental double call during the same
 * signup flow while still allowing a future signup after a fresh page load.
 */
export function trackSignupCompleted(): void {
  if (process.env.VUE_APP_TRACKING_ENABLED !== "on" || typeof window === "undefined" ||
      isPrivateTrackingContext(window.location.pathname + window.location.search)) return;
  if (
    typeof window === "undefined" ||
    typeof document === "undefined" ||
    signupCompletionTracked
  ) {
    return;
  }

  signupCompletionTracked = true;
  cleanupPublicAuthListeners();
  void ensureGtmLoadNow();

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "myculture_signup_completed",
    ...getHeroAttribution(),
    ...getLandingAttribution(),
  });
}
